/**
 * Storage quota arithmetic.
 *
 * The enforcement itself is a single conditional SQL UPDATE (see
 * `packages/db/src/repositories/storage.ts` and docs/DATABASE.md) — that is
 * what makes it race-free. This module holds the pure maths around it:
 * classification, projection and presentation.
 */

import type { Variant } from "./media.js";

export const STORAGE_CLASSES = ["originals", "derivatives", "video", "exports"] as const;
export type StorageClass = (typeof STORAGE_CLASSES)[number];

export interface StorageUsage {
  limitBytes: number;
  usedBytes: number;
  /** Bytes held by uploads that are in flight but not yet committed. */
  reservedBytes: number;
  byClass: Record<StorageClass, number>;
}

/**
 * Which bucket a variant is billed to. Every derivative counts against the
 * limit: charging only for originals lets a group's real footprint drift about
 * 35% above its plan, which is how storage bills become surprises.
 */
export function storageClassForVariant(variant: Variant): StorageClass {
  if (variant === "original") return "originals";
  if (variant === "video_720" || variant === "video_1080") return "video";
  return "derivatives";
}

export interface QuotaCheck {
  allowed: boolean;
  /** Bytes still available after in-flight reservations. */
  availableBytes: number;
  shortfallBytes: number;
}

export function checkQuota(usage: StorageUsage, requestedBytes: number): QuotaCheck {
  const committed = usage.usedBytes + usage.reservedBytes;
  const available = Math.max(0, usage.limitBytes - committed);
  const allowed = requestedBytes <= available;
  return {
    allowed,
    availableBytes: available,
    shortfallBytes: allowed ? 0 : requestedBytes - available,
  };
}

/** 0–1. Reservations are included so the meter reflects what is really spoken for. */
export function usageRatio(usage: StorageUsage): number {
  if (usage.limitBytes <= 0) return 1;
  return Math.min(1, (usage.usedBytes + usage.reservedBytes) / usage.limitBytes);
}

export type QuotaLevel = "ok" | "warning" | "critical" | "full";

export function quotaLevel(usage: StorageUsage): QuotaLevel {
  const ratio = usageRatio(usage);
  if (ratio >= 1) return "full";
  if (ratio >= 0.95) return "critical";
  if (ratio >= 0.8) return "warning";
  return "ok";
}

/**
 * Preset quotas offered in the Super Admin panel (§7), plus a custom value.
 * Stored and compared in bytes; never trust a value that arrived as "500 GB".
 */
export const QUOTA_PRESETS = [
  { label: "100 GB", bytes: 100 * 1024 ** 3 },
  { label: "500 GB", bytes: 500 * 1024 ** 3 },
  { label: "1 TB", bytes: 1024 ** 4 },
  { label: "2 TB", bytes: 2 * 1024 ** 4 },
  { label: "5 TB", bytes: 5 * 1024 ** 4 },
] as const;

export const MIN_QUOTA_BYTES = 1024 ** 3; // 1 GB
export const MAX_QUOTA_BYTES = 100 * 1024 ** 4; // 100 TB

/**
 * Lowering a quota below current usage is permitted: it stops new uploads but
 * never deletes anything. Silently discarding a group's photos because an
 * administrator typed a smaller number would be indefensible.
 */
export function validateQuotaChange(
  newLimitBytes: number,
  usage: StorageUsage,
): { valid: boolean; reason?: string; willBeOverLimit: boolean } {
  if (!Number.isSafeInteger(newLimitBytes)) {
    return { valid: false, reason: "Quota must be a whole number of bytes.", willBeOverLimit: false };
  }
  if (newLimitBytes < MIN_QUOTA_BYTES) {
    return { valid: false, reason: "Quota cannot be below 1 GB.", willBeOverLimit: false };
  }
  if (newLimitBytes > MAX_QUOTA_BYTES) {
    return { valid: false, reason: "Quota cannot exceed 100 TB.", willBeOverLimit: false };
  }
  return { valid: true, willBeOverLimit: newLimitBytes < usage.usedBytes };
}

const UNITS = ["B", "KB", "MB", "GB", "TB", "PB"] as const;

/**
 * Human byte formatting. Uses 1024-based units with the familiar short labels,
 * matching what desktop operating systems show users.
 */
export function formatBytes(bytes: number, fractionDigits?: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";

  const exponent = Math.min(UNITS.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** exponent;
  const digits = fractionDigits ?? (exponent === 0 ? 0 : value >= 100 ? 0 : value >= 10 ? 1 : 2);

  return `${value.toFixed(digits)} ${UNITS[exponent]}`;
}

/**
 * The warning shown before an original download (§11). Anything above a few
 * megabytes on a metered connection deserves a heads-up.
 */
export const ORIGINAL_DOWNLOAD_WARNING_BYTES = 3 * 1024 * 1024;

export function shouldWarnBeforeOriginal(bytes: number): boolean {
  return bytes >= ORIGINAL_DOWNLOAD_WARNING_BYTES;
}
